import { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import $ from "jquery";
import { Mixin, useScrollableMixin } from "brew-js-react";
import { combineRef, useFormField, useMemoizedFunction, useRefInitCallback } from "zeta-dom-react";
import { runCSSTransition } from "zeta-dom/cssUtil";
import dom, { retainFocus } from "zeta-dom/dom";
import { exclude } from "zeta-dom/util";
import { setClass, scrollIntoView, removeNode, getRect } from "zeta-dom/domUtil";
import { Icon, IconNames, Loading } from "src/components/common";
import { getValueOrDefault } from 'src/util/common';
import { TextField, TextFieldProps } from ".";
import { useTranslation } from "src/i18n";

const flyout: HTMLElement = $('<div class="eac-suggestion-picker">')[0];

export interface Suggestion<T = any> {
    displayText: string;
    matchingText?: readonly string[];
    value: T;
    category?: string;
    subcategory?: string;
    menu?: any;
    source?: any;
}

export interface SuggestionsProps<T extends Suggestion> extends TextFieldProps {
    suggestions: T[];
    loading?: boolean;
    showCount?: number;
    showAllWhenEmpty?: boolean;
    getItemProps: (props: SuggestionItemProps<T>, item: T) => SuggestionItemProps<T>;
    onSelectedItem?: (v: T) => void;
    tab?: string;
}

export interface SuggestionItemProps<T> {
    formattedText: string;
    value: T extends Suggestion<infer V> ? V : never;
    icon?: IconNames;
    onClick: () => void;
    category?: any,
    subcategory?: any,
    displayText?: string,
    menu?: any,
    source?: any;
    tab?: any;
}

function fuzzyMatch(haystack: string, needle: string) {
    let str: string;
    haystack = getValueOrDefault(haystack, '');
    needle = getValueOrDefault(needle, '');
    str = haystack.toLowerCase();

    function matchAt(j: number) {
        const vector: number[] = [];
        for (let i = 0, lastpos = -1, len = needle.length; i < len; i++) {
            let l = needle.charAt(i).toLowerCase();
            if (l === ' ') {
                continue;
            }
            j = str.indexOf(l, j);
            if (j === -1) {
                return {
                    firstIndex: Infinity,
                    consecutiveMatches: -1,
                    formattedText: haystack
                };
            }
            vector[vector.length] = j - lastpos - 1;
            lastpos = j++;
        }
        let firstIndex = vector[0];
        let consecutiveMatches = /^(0+)/.test(vector.slice(0).sort((a, b) => a - b).join('')) && RegExp.$1.length + 1;
        return {
            firstIndex: firstIndex,
            consecutiveMatches: +getValueOrDefault(consecutiveMatches, 1),
            get formattedText() {
                let formattedText = '';
                for (let i = 0, k = 0; i < vector.length; i++) {
                    formattedText += haystack.substr(k, vector[i]) + '<b>' + haystack[k + vector[i]] + '</b>';
                    k += vector[i] + 1;
                }
                formattedText += haystack.slice(j);
                formattedText = formattedText.replace(/<\/b><b>/g, '');
                Object.defineProperty(this, 'formattedText', { value: formattedText });
                return formattedText;
            }
        };
    }

    let cur = matchAt(0);
    let result = cur;
    while (cur.firstIndex !== Infinity) {
        cur = matchAt(cur.firstIndex + 1);
        if (cur.consecutiveMatches > result.consecutiveMatches) {
            result = cur;
        }
    }
    return result;
}

function processSuggestions<T extends Suggestion>(suggestions: T[], needle: string) {
    const items = suggestions.map(function (v) {
        const result = {
            ...v,
            ...fuzzyMatch(v.displayText, needle)
        };
        (v.matchingText || []).forEach(str => {
            const m = fuzzyMatch(str, needle);
            result.firstIndex = Math.min(result.firstIndex, m.firstIndex);
            result.consecutiveMatches = Math.max(result.consecutiveMatches, m.consecutiveMatches);
        });
        return result;
    });
    return items.filter(v => v.firstIndex !== Infinity)
        .sort((a, b) => (b.consecutiveMatches - a.consecutiveMatches) || (a.firstIndex - b.firstIndex) || a.displayText.localeCompare(b.displayText));
}

export function SuggestionItem<T>(props: SuggestionItemProps<T>) {
    const { t } = useTranslation();
    const matchObj: any = {
        'department': t('formAndLink.label.catergories.department'),
        'reportlink': `${t('myPerf.title.nocampaign')} > ${t('myPerf.section.report')}`,
        'quicklink': `${t('myPerf.title.nocampaign')} > ${t('myPerf.section.quickLinks')}`,
        'eservice': t('formAndLink.label.catergories.eService'),
        'customer-related': t('formAndLink.label.option.eservice.customer'),
        'tools': t('formAndLink.label.option.eservice.tools'),
        'leader-only': t('formAndLink.label.option.eservice.leader'),
        'forms': `${t('formAndLink.title')} > ${t('formAndLink.label.catergories.forms')}`,
        'useful-links': t('formAndLink.title'),
        'software': `${t('formAndLink.title')} > ${t('formAndLink.label.catergories.software')}`,
        'manuals': t('formAndLink.label.option.usefullink.manuals'),
        'bulletin': t('formAndLink.label.option.usefullink.bulletin'),
        'agent-personal': t('formAndLink.label.option.eservice.tools'),
        'products': t('common.mainMenu.products'),
        'personal': `${t('common.userMenu.settings')} > ${t('setting.section.personal')}`,
        'security': `${t('common.userMenu.settings')} > ${t('setting.section.security')}`,
        'mcp': `${t('formAndLink.label.catergories.eService')} > ${t('formAndLink.label.option.eservice.customer')} > ${t('common.search.result.customer')}`
    }

    const matchMcp = matchObj['mcp'] || ''
    const matchProducts = matchObj['products'] || ''
    const matchCategory = matchObj[props.category] || ''
    const matchSubcategory = matchObj[props.subcategory] || ''
    const jian = !props.subcategory || props.subcategory === 'others' ? '' : ' > '
    function showResult() {
        if (props.source.startsWith('mcp') && props.tab !== 'customer') {
            return matchMcp
        } else if (props.menu?.startsWith('products')) {
            return matchProducts
        } else {
            return `${matchCategory}
                    ${jian}
                    ${matchSubcategory}`
        }
    }

    return (
        <button type="button" className="eac-suggestion-picker-item" onClick={props.onClick}>
            <div className="eac-suggestion-picker-item-classify">
                <span dangerouslySetInnerHTML={{ __html: props.formattedText }}></span>
                <span>{showResult()}</span>
            </div>
            {props.icon && <Icon icon={props.icon} />}
        </button>
    );
}

function SuggestionList<T extends Suggestion>(props: { inputText: string } & SuggestionsProps<T>) {
    const scrollable = useScrollableMixin({ direction: 'y-only' });
    const suggestions = !props.showAllWhenEmpty && !props.inputText ? [] : processSuggestions(props.suggestions, props.inputText);
    if (props.showCount) {
        suggestions.splice(props.showCount);
    }

    useEffect(() => {
        let container = scrollable.elements()[0];
        setClass(container.parentElement!, {
            loading: !!props.loading,
            empty: !suggestions[0]
        });
        if (suggestions.length > 0) {
            container.style.maxHeight = (getRect().bottom - getRect(container).top - 10) + 'px';
        }
    });

    return (
        <div {...Mixin.use(scrollable)}>
            <div {...Mixin.use(Mixin.scrollableTarget, 'eac-suggestion-picker-list')}>
                {suggestions.map((v, i) => (
                    <SuggestionItem key={i} {...props.getItemProps({
                        onClick: () => props.onSelectedItem?.(v),
                        formattedText: v.formattedText,
                        value: v.value,
                        menu: v.menu,
                        category: v.category,
                        subcategory: v.subcategory,
                        displayText: v.displayText,
                        source: v.source
                    }, v)} tab={props.tab} />
                ))}
            </div>
            {props.loading && <Loading />}
        </div>
    );
}

export function Suggestions<T extends Suggestion>(props: SuggestionsProps<T>) {
    const { value, error, setValue, elementRef } = useFormField(props, '');
    const focused = useRef(false);
    const updatePicker = useMemoizedFunction(() => {
        ReactDOM.render(<SuggestionList {...props} inputText={value} />, flyout);
    });

    useEffect(() => {
        if (focused.current) {
            updatePicker();
        }
    }, [value, updatePicker, props.suggestions, props.loading]);

    return (
        <TextField ref={combineRef(elementRef, useRefInitCallback(initEvent))} {...exclude(props, ['name'])}
            autoComplete="off" value={value} error={error} onChange={setValue} />
    );

    function initEvent(element: HTMLElement) {
        const input = element.querySelector('input')!;
        dom.on(element, 'focusin', () => {
            focused.current = true;
            element.appendChild(flyout);
            updatePicker();
            retainFocus(input, flyout);
            scrollIntoView(flyout);
            setClass(flyout, 'open', true);
            setClass(flyout, 'closing', false);
        });
        dom.on(element, 'focusout', () => {
            focused.current = false;
            runCSSTransition(flyout, 'closing', true).then(() => {
                setClass(flyout, 'open', false);
                if (element.contains(flyout.parentElement)) {
                    removeNode(flyout);
                }
            });
        });
        dom.on(element, 'keystroke', (e) => {
            let cur = flyout.querySelector('button.active');
            if (cur) {
                setClass(cur, 'active', false);
            }
            switch (e.data) {
                case 'downArrow':
                    setClass(cur?.nextElementSibling || flyout.querySelector('button')!, 'active', true);
                    return true;
                case 'upArrow':
                    setClass(cur?.previousElementSibling || flyout.querySelector('button:last-child')!, 'active', true);
                    return true;
                case 'enter':
                    return cur && $(cur).trigger('click');
            }
        });
    }
}
